/**
 * API Client with Token Interceptor (Story 1.6)
 *
 * A fetch wrapper that:
 * - Attaches Authorization header with access token
 * - Automatically refreshes token on 401 responses
 * - Queues requests during refresh to prevent multiple refresh calls
 *
 * USAGE:
 * ```typescript
 * const response = await apiClient('/api/protected/resource', {
 *   method: 'GET'
 * });
 * ```
 */

import { useAuthStore } from "../stores/authStore";
import { refreshAccessToken, logout } from "./auth";
import { apiUrl } from "./apiBase";

// Refresh state management
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Queue for requests waiting for token refresh
type QueueItem = {
  resolve: (value: Response | PromiseLike<Response>) => void;
  reject: (reason?: unknown) => void;
  input: RequestInfo | URL;
  init?: RequestInit;
};
const requestQueue: QueueItem[] = [];

/**
 * Process queued requests after token refresh
 */
async function processQueue(newToken: string | null): Promise<void> {
  for (const item of requestQueue) {
    if (newToken) {
      try {
        const response = await fetchWithToken(item.input, item.init, newToken);
        item.resolve(response);
      } catch (error) {
        item.reject(error);
      }
    } else {
      item.reject(new Error("Token refresh failed"));
    }
  }
  // Clear the queue
  requestQueue.length = 0;
}

/**
 * Make a fetch request with the provided token
 */
async function fetchWithToken(
  input: RequestInfo | URL,
  init?: RequestInit,
  token?: string
): Promise<Response> {
  const tokenToUse = token ?? useAuthStore.getState().getAccessToken();

  const headers = new Headers(init?.headers);

  if (tokenToUse) {
    headers.set("Authorization", `Bearer ${tokenToUse}`);
  }

  // Relative "/api/..." strings must target the API worker, not the Pages
  // origin (which serves index.html for every path). apiUrl() prepends the
  // configured base in prod and is a no-op in dev (Vite proxy).
  const resolvedInput = typeof input === "string" ? apiUrl(input) : input;

  return fetch(resolvedInput, {
    ...init,
    headers,
    credentials: "include", // Always include cookies for refresh token
  });
}

/**
 * Handle token refresh with request queuing
 */
async function handleTokenRefresh(): Promise<string | null> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = refreshAccessToken();
  }

  const result = await refreshPromise;
  isRefreshing = false;
  refreshPromise = null;
  return result;
}

/**
 * API Client with automatic token handling
 *
 * Features:
 * - Attaches access token to requests
 * - Retries on 401 after token refresh
 * - Queues concurrent requests during refresh
 * - Redirects to login on refresh failure
 *
 * @param input - Request URL or Request object
 * @param init - Request options
 * @returns Fetch Response
 *
 * @example
 * ```typescript
 * // GET request
 * const response = await apiClient('/api/clients');
 * const data = await response.json();
 *
 * // POST request
 * const response = await apiClient('/api/clients', {
 *   method: 'POST',
 *   body: JSON.stringify({ name: 'New Client' }),
 * });
 * ```
 */
export async function apiClient(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  // Make the initial request
  const response = await fetchWithToken(input, init);

  // If not 401, return the response
  if (response.status !== 401) {
    return response;
  }

  // Handle 401 - attempt token refresh
  // Check if we're already refreshing
  if (isRefreshing) {
    // Queue this request and wait for refresh to complete
    return new Promise<Response>((resolve, reject) => {
      requestQueue.push({ resolve, reject, input, init });
    });
  }

  // Attempt to refresh the token
  const newToken = await handleTokenRefresh();

  // Process any queued requests
  await processQueue(newToken);

  if (newToken) {
    // Retry the original request with new token
    return fetchWithToken(input, init, newToken);
  }

  // Refresh failed - redirect to login
  logout();

  // Return the original 401 response
  return response;
}

/**
 * GET request helper
 */
export function apiGet(url: string, init?: RequestInit): Promise<Response> {
  return apiClient(url, { ...init, method: "GET" });
}

/**
 * POST request helper
 */
export function apiPost(
  url: string,
  body?: unknown,
  init?: RequestInit
): Promise<Response> {
  return apiClient(url, {
    ...init,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PUT request helper
 */
export function apiPut(
  url: string,
  body?: unknown,
  init?: RequestInit
): Promise<Response> {
  return apiClient(url, {
    ...init,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PATCH request helper
 */
export function apiPatch(
  url: string,
  body?: unknown,
  init?: RequestInit
): Promise<Response> {
  return apiClient(url, {
    ...init,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * DELETE request helper
 */
export function apiDelete(url: string, init?: RequestInit): Promise<Response> {
  return apiClient(url, { ...init, method: "DELETE" });
}
